import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { Modal } from "../shared/Modal";
import { SearchSelect } from "../waba/MetricsTab";
import { toastError } from "../shared/toast";
import {
  FILE_ACCEPT, SubjectInput, errMsg, fileProblem, fmtSize, ghostBtn, inputStyle, labelStyle,
  primaryBtn, todaySp, uploadAttachment, useTaskOptions,
} from "./common";

export function TaskCreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const qc = useQueryClient();
  const { data: opts } = useTaskOptions();
  const [agentId, setAgentId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [subjects, setSubjects] = useState<string[]>([]);
  const [day, setDay] = useState(todaySp());
  const [time, setTime] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const agentOptions = (opts?.agents ?? []).map(a => ({ value: a.id, label: `${a.name}${a.manager_name ? ` · ${a.manager_name}` : ""}` }));
  const ok = !!agentId && !!title.trim() && !!day && !busy;

  function pick(list: FileList | null) {
    if (!list) return;
    // Copia antes de limpar o input: o FileList é vivo e esvazia com value = "".
    const picked = Array.from(list);
    setErr(picked.map(fileProblem).find(Boolean) ?? null);
    setFiles(prev => [...prev, ...picked.filter(f => !fileProblem(f))]);
    if (fileInput.current) fileInput.current.value = "";
  }

  async function submit() {
    setErr(null);
    setBusy("Criando…");
    let id: string;
    try {
      ({ id } = await api.post<{ id: string }>("/tasks", {
        agent_id: agentId, title, description, subjects, due_date: day, due_time: time || null,
      }));
    } catch (e) {
      setErr(errMsg(e));
      setBusy(null);
      return;
    }
    // Tarefa já existe: falha de anexo não desfaz a criação (dá pra anexar de novo no modal).
    const failed: string[] = [];
    for (const [i, f] of files.entries()) {
      setBusy(`Enviando anexo ${i + 1}/${files.length}…`);
      try { await uploadAttachment(id, f); } catch { failed.push(f.name); }
    }
    qc.invalidateQueries({ queryKey: ["tasks"] });
    if (failed.length) toastError(`Tarefa criada, mas estes anexos falharam: ${failed.join(", ")}`);
    onCreated(id);
  }

  return (
    <Modal onClose={onClose} zIndex={1000}>
      <div style={{ width: 640, maxWidth: "100%", maxHeight: "calc(100vh - 48px)", overflowY: "auto", background: "var(--surface-card)", border: "1px solid var(--line-strong)", borderRadius: 12, boxShadow: "var(--shadow-modal)", padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: "var(--ink)", flex: 1 }}>Nova tarefa</h2>
          <button onClick={onClose} aria-label="Fechar" style={{ background: "none", border: "none", color: "var(--ink-muted)", fontSize: 22, lineHeight: 1, cursor: "pointer" }}>×</button>
        </div>

        <div>
          <span style={labelStyle}>Agente</span>
          <SearchSelect value={agentId} onChange={setAgentId} options={agentOptions} placeholder="Selecione o agente" />
        </div>
        <div>
          <label style={labelStyle} htmlFor="task-title">Título</label>
          <input id="task-title" value={title} maxLength={200} onChange={e => setTitle(e.target.value)} style={inputStyle} placeholder="O que precisa ser feito?" />
        </div>
        <div>
          <span style={labelStyle}>Assuntos</span>
          <SubjectInput value={subjects} onChange={setSubjects} />
        </div>
        <div>
          <span style={labelStyle}>Prazo</span>
          <div style={{ display: "flex", gap: 8 }}>
            <input type="date" value={day} min={todaySp()} onChange={e => setDay(e.target.value)} style={{ ...inputStyle, width: 170 }} />
            <input type="time" value={time} onChange={e => setTime(e.target.value)} style={{ ...inputStyle, width: 120 }} aria-label="Hora (opcional)" />
            <span style={{ fontSize: 12, color: "var(--ink-muted)", alignSelf: "center" }}>hora opcional</span>
          </div>
        </div>
        <div>
          <label style={labelStyle} htmlFor="task-desc">Descrição (markdown)</label>
          <textarea id="task-desc" rows={6} value={description} onChange={e => setDescription(e.target.value)} style={{ ...inputStyle, resize: "vertical" }} placeholder="Contexto, passos, links… **negrito**, listas com -" />
        </div>
        <div>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
            <span style={{ ...labelStyle, marginBottom: 0, flex: 1 }}>Anexos</span>
            <input ref={fileInput} type="file" multiple accept={FILE_ACCEPT} hidden onChange={e => pick(e.target.files)} />
            <button type="button" onClick={() => fileInput.current?.click()} style={{ ...ghostBtn, padding: "5px 12px", fontSize: 12 }}>+ Adicionar</button>
          </div>
          <div style={{ fontSize: 11, color: "var(--ink-muted)", marginBottom: 6 }}>Imagens até 10 MB · vídeos (mp4/webm/mov) até 100 MB · pdf/office/csv/txt até 25 MB</div>
          {files.map((f, i) => (
            <div key={`${f.name}-${i}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, padding: "4px 0", color: "var(--ink)" }}>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
              <span style={{ color: "var(--ink-muted)" }}>{fmtSize(f.size)}</span>
              <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label={`Remover ${f.name}`} style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer", fontSize: 15 }}>×</button>
            </div>
          ))}
        </div>

        {err && <span style={{ fontSize: 12, color: "var(--danger)" }}>{err}</span>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
          {busy && <span style={{ fontSize: 12, color: "var(--ink-muted)", marginRight: "auto" }}>{busy}</span>}
          <button onClick={onClose} style={ghostBtn}>Cancelar</button>
          <button onClick={submit} disabled={!ok} style={{ ...primaryBtn, opacity: ok ? 1 : 0.6 }}>Criar tarefa</button>
        </div>
      </div>
    </Modal>
  );
}
